import { post } from './client';
import { API_ENDPOINTS } from '../config/api';
import { urlBase64ToUint8Array } from '../utils/push';

interface VapidKeyResponse {
  error?: { type?: string; message?: string };
  public_key: string;
}

interface PushSubscribeResponse {
  error?: { type?: string; message?: string };
  success: boolean;
}

interface PushUnsubscribeResponse {
  error?: { type?: string; message?: string };
  success: boolean;
}

export async function getVapidPublicKey(): Promise<string | null> {
  const response = await post<VapidKeyResponse>(API_ENDPOINTS.PUSH_VAPID_KEY, {});
  if (response.ok && response.data?.public_key) {
    return response.data.public_key;
  }
  return null;
}

export async function subscribePush(loginKey: string): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }

  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false;
  }

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(vapidKey);

  // Clear any existing subscription with a different key to avoid DOMException
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    await existingSubscription.unsubscribe();
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
  });

  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (!key || !auth) {
    return false;
  }

  const response = await post<PushSubscribeResponse>(API_ENDPOINTS.PUSH_SUBSCRIBE, {
    login_key: loginKey,
    endpoint: subscription.endpoint,
    p256dh: btoa(String.fromCharCode(...new Uint8Array(key))),
    auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
  });

  return response.ok && response.data?.success === true;
}

export async function unsubscribePush(loginKey: string): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return true;
  }

  const response = await post<PushUnsubscribeResponse>(API_ENDPOINTS.PUSH_UNSUBSCRIBE, {
    login_key: loginKey,
    endpoint: subscription.endpoint,
  });

  await subscription.unsubscribe();

  return response.ok && response.data?.success === true;
}
